import mockReleaseData from "../data/mockData/releasesData";
import mockReleaseDetailsData from "../data/mockData/releaseDetailsData";

const DEMO_EAN_PREFIX = "42511648";

function readJson(key, fallback) {
  if (typeof sessionStorage === "undefined") {
    return fallback;
  }
  try {
    const raw = sessionStorage.getItem(key);
    if (raw == null) {
      return fallback;
    }
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function mergeById(baseRows, extras) {
  const map = new Map();
  (baseRows || []).forEach(row => {
    if (row?.id != null) {
      map.set(String(row.id), row);
    }
  });
  (extras || []).forEach(row => {
    if (row?.id != null) {
      map.set(String(row.id), { ...map.get(String(row.id)), ...row });
    }
  });
  return Array.from(map.values());
}

/** EAN-13 / UPC-A check digit (weights 1,3 from the left). */
export function computeEanCheckDigit(valueWithoutCheck) {
  const digits = String(valueWithoutCheck || "").replace(/\D/g, "");
  if (!digits.length) {
    return "";
  }

  let sum = 0;
  for (let i = 0; i < digits.length; i += 1) {
    const digit = parseInt(digits.charAt(i), 10);
    sum += digit * (i % 2 === 0 ? 1 : 3);
  }

  return String((10 - (sum % 10)) % 10);
}

export function isValidEanChecksum(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!/^(\d{12}|\d{13})$/.test(digits)) {
    return false;
  }

  const body = digits.slice(0, -1);
  const check = digits.slice(-1);
  return computeEanCheckDigit(body) === check;
}

export function collectUsedReleaseEans(excludeReleaseId = null) {
  const used = new Set();
  const excludeSid = excludeReleaseId != null ? String(excludeReleaseId) : null;

  const addEan = (ean, releaseId) => {
    const normalized = String(ean || "").trim();
    if (!normalized) {
      return;
    }
    if (excludeSid && String(releaseId) === excludeSid) {
      return;
    }
    used.add(normalized);
  };

  mockReleaseData.forEach(row => addEan(row.ean, row.id));
  mockReleaseDetailsData.forEach(row => addEan(row.ean, row.id));

  const listExtras = readJson("wl_bs_releases_extras", []);
  mergeById(mockReleaseData, listExtras).forEach(row => addEan(row.ean, row.id));

  const details = readJson("wl_bs_release_details_by_id", {});
  Object.entries(details).forEach(([releaseId, detail]) => {
    addEan(detail?.ean, releaseId);
  });

  return used;
}

/** Random 13-digit EAN matching seed catalog pattern (42511648xxxxC). */
export function generateUniqueDemoEan() {
  const used = collectUsedReleaseEans();

  for (let attempt = 0; attempt < 500; attempt += 1) {
    const middle = String(Math.floor(Math.random() * 10000)).padStart(4, "0");
    const base12 = `${DEMO_EAN_PREFIX}${middle}`;
    const ean = `${base12}${computeEanCheckDigit(base12)}`;
    if (!used.has(ean) && isValidEanChecksum(ean)) {
      return ean;
    }
  }

  const fallbackMiddle = String(Date.now() % 10000).padStart(4, "0");
  const fallbackBase12 = `${DEMO_EAN_PREFIX}${fallbackMiddle}`;
  return `${fallbackBase12}${computeEanCheckDigit(fallbackBase12)}`;
}

export function validateDemoEanUpc(eanUpc, excludeReleaseId = null) {
  const value = String(eanUpc || "").trim();

  if (!value) {
    return { valid: false, message: "EAN/UPC is required." };
  }

  if (!isValidEanChecksum(value)) {
    return { valid: false, message: "Invalid EAN/UPC number." };
  }

  const used = collectUsedReleaseEans(excludeReleaseId);
  if (used.has(value)) {
    return { valid: false, message: "This EAN/UPC is already in use." };
  }

  return { valid: true, message: "" };
}

export function resolveDemoReleaseEan(body = {}) {
  const generateEan =
    body.generate_ean === true ||
    body.generate_ean === "true" ||
    body.generate_ean === 1 ||
    body.generate_ean === "1";

  if (generateEan) {
    return {
      ean: generateUniqueDemoEan(),
      generate_ean: true,
    };
  }

  const ean = body.ean != null ? String(body.ean).trim() : "";
  return {
    ean,
    generate_ean: false,
  };
}
